/*
 * ============================================================================
 * FROTHER AUTO-ADD · Layer 2 — cart reconciler (Horizon theme)
 * Loaded globally (layout/theme.liquid) so it runs on the cart PAGE and the
 * cart DRAWER. Surface-agnostic: reads /cart.js and rides the theme's
 * cart:update event, which both surfaces emit.
 * ----------------------------------------------------------------------------
 * Gives a complimentary Frother to FIRST-TIME Collagen Pro buyers.
 *
 * Reads window.customerHasCollagenBefore (set by Layer 1 Liquid) and the live
 * cart, then keeps the cart in line with these rules:
 *   • Collagen present + eligible + no frother yet  -> auto-add one frother
 *   • Frother quantity bumped above 1                -> cap it back to one
 *   • Collagen (direct or bundle) gone               -> remove EVERY frother
 *   • Logs in + prior Collagen history (revalidate)  -> remove EVERY frother
 *   • Guest checks out with the gift                  -> route to login -> /cart
 *   • Out of stock / add fails                       -> friendly message, no breakage
 *   • Shopper removes our frother themselves          -> respect it, do not re-add
 *
 * Eligibility fails CLOSED: only window.customerHasCollagenBefore === false
 * (a confirmed first-timer, or a logged-out guest per spec) is eligible.
 * undefined / true -> not eligible (protects margin).
 *
 * Checkout gate: eligibility is only knowable once authenticated, so a GUEST
 * clicking checkout with the gift in cart is sent through login and back to
 * /cart, where the reconciler validates the gift BEFORE checkout — instead of
 * the gift silently vanishing later in the checkout flow. Logged-in shoppers
 * are reconciled on add and check out untouched. (Accelerated buttons such as
 * Shop Pay render in isolated frames and bypass this gate — see notes.)
 *
 * Re-render strategy: this theme (Horizon) uses Ajax + Web Components, so we
 * dispatch the theme's own `cart:update` event to morph the cart section in
 * place (no full-page reload, preserves focus / no layout flash). The cart
 * drawer auto-opens on `cart:update`, so we briefly suspend that around our
 * own dispatch to avoid popping the drawer on the cart page.
 * ============================================================================
 */
(function () {
  'use strict';

  /* ============================ CONFIG ============================ */
  // Frother VARIANT id to auto-add (qty 1). MERCHANT-EDITABLE via the theme
  // setting "Free Frother Gift → Free frother product"; the picked product's
  // first available variant id is injected by snippets/frother-auto-add.liquid as
  // window.VIVANA_FROTHER_VARIANT_ID. The default below is the fallback when the
  // setting is unset. NOTE: this must be a VARIANT id — the product id
  // 15790792212811 must NOT be used here; /cart/add.js fails on a product id.
  var DEFAULT_FROTHER_VARIANT_ID = 57555940540747;
  var FROTHER_VARIANT_ID = Number(window.VIVANA_FROTHER_VARIANT_ID) || DEFAULT_FROTHER_VARIANT_ID;

  // TRIGGER PRODUCT ids — any product whose presence in the cart grants the
  // complimentary frother. This is Collagen Pro itself PLUS any bundle that
  // contains it (a bundle line item carries the BUNDLE's product_id, not
  // Collagen's, so each such bundle must be listed). MERCHANT-EDITABLE in the
  // theme editor (Theme settings → "Free Frother Gift"); the chosen product ids
  // are injected by snippets/frother-auto-add.liquid as
  // window.VIVANA_FROTHER_TRIGGER_PRODUCT_IDS. The defaults below are the
  // fallback when the setting is empty (behaviour matches the original build):
  //   15484170961227 — Collagen Pro (direct)
  //   15838297588043 — THE COMPLETE SYSTEM – TAKE FULL CONTROL
  //   15832357208395 — THE RESILIENCE SYSTEM – RECOVER AND REBUILD
  //   15832363336011 — THE HRT COMPANION – SPECIALISED
  var DEFAULT_TRIGGER_PRODUCT_IDS = [15484170961227, 15838297588043, 15832357208395, 15832363336011];
  var TRIGGER_PRODUCT_IDS =
    Array.isArray(window.VIVANA_FROTHER_TRIGGER_PRODUCT_IDS) && window.VIVANA_FROTHER_TRIGGER_PRODUCT_IDS.length
      ? window.VIVANA_FROTHER_TRIGGER_PRODUCT_IDS.map(Number)
      : DEFAULT_TRIGGER_PRODUCT_IDS;

  var AUTO_FLAG = '_auto_added_frother'; // hidden marker on frothers WE add
  var SS_ADDED = 'frother_auto_added';     // session flag: we added a frother
  var SS_DISMISSED = 'frother_auto_dismissed'; // session flag: shopper removed it
  var EVENT_SOURCE = 'frother-auto-add';
  /* ================================================================ */

  // Re-entrancy guard. Combined with state convergence, prevents loops.
  var busy = false;

  /* ----------------------------- Session state ----------------------------- */
  function ssGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null; // privacy mode / disabled storage: degrade gracefully
    }
  }
  function ssSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* no-op */
    }
  }
  function ssRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* no-op */
    }
  }

  /* ------------------------------ Cart helpers ------------------------------ */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // True if any TRIGGER product is in the cart — Collagen Pro directly OR a
  // bundle that contains it (both carry a product_id in TRIGGER_PRODUCT_IDS).
  function cartHasCollagen(cart) {
    return cart.items.some(function (item) {
      return TRIGGER_PRODUCT_IDS.indexOf(item.product_id) !== -1;
    });
  }

  // Frothers that WE auto-added (used to track our gift for the session flag).
  function autoAddedFrothers(cart) {
    return cart.items.filter(function (item) {
      var props = item.properties || {};
      return item.variant_id === FROTHER_VARIANT_ID && props[AUTO_FLAG] === 'true';
    });
  }

  // Every Frother line in the cart, regardless of who added it.
  function frotherLineItems(cart) {
    return cart.items.filter(function (item) {
      return item.variant_id === FROTHER_VARIANT_ID;
    });
  }

  // Is ANY frother already in the cart (auto-added or customer-added)?
  function frotherInCart(cart) {
    return frotherLineItems(cart).length > 0;
  }

  /* -------------------------------- Mutations ------------------------------- */
  function addFrother() {
    var props = {};
    props[AUTO_FLAG] = 'true';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: FROTHER_VARIANT_ID, quantity: 1, properties: props }] }),
    }).then(function (res) {
      // CRITICAL: fetch does NOT throw on 4xx. 422 = sold out / exceeds stock.
      if (!res.ok) {
        return res.json().then(function (err) {
          throw new Error((err && err.description) || 'Frother is unavailable right now.');
        });
      }
      return res.json();
    });
  }

  // Remove the given line items by their unique `key`, one after another.
  function removeLineItemsByKey(keys) {
    return keys.reduce(function (chain, key) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      });
    }, Promise.resolve());
  }

  // Set explicit quantities for a list of [{ key, quantity }] ops, in series.
  // Used to cap the complimentary frother back to a single unit.
  function setLineQuantities(ops) {
    return ops.reduce(function (chain, op) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: op.key, quantity: op.quantity }),
        });
      });
    }, Promise.resolve());
  }

  /* ------------------------ Native re-render (no reload) -------------------- */
  // Dispatch the theme's own cart:update so the cart section morphs in place
  // and the header cart bubble updates. Suspend the drawer's auto-open just for
  // this synchronous dispatch so we don't pop the drawer on the cart page.
  function notifyCartUpdated(cart) {
    var drawers = document.querySelectorAll('cart-drawer-component[auto-open]');
    var i;
    for (i = 0; i < drawers.length; i++) drawers[i].removeAttribute('auto-open');

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: EVENT_SOURCE,
          data: { source: EVENT_SOURCE, itemCount: cart.item_count },
        },
      })
    );

    // Restore on the next frame — the dispatch above is synchronous, so all
    // listeners (including the drawer) have already run by now.
    window.requestAnimationFrame(function () {
      for (i = 0; i < drawers.length; i++) drawers[i].setAttribute('auto-open', '');
    });
  }

  /* ----------------------- Friendly messaging (ADA) ------------------------- */
  function showMessage(msg) {
    var el = document.getElementById('frother-auto-msg');
    if (!el) {
      el = document.createElement('div');
      el.id = 'frother-auto-msg';
      el.setAttribute('role', 'status'); // polite live region, announced by AT
      el.style.cssText =
        'margin:12px auto;padding:10px 14px;border-radius:8px;background:#f6f6f7;' +
        'color:#1a1a1a;font-size:14px;line-height:1.4;text-align:center;' +
        'max-width:var(--page-width, 1200px);';
      // Insert into a stable host OUTSIDE the morphed cart section so a later
      // re-render cannot wipe it.
      var host = document.getElementById('MainContent') || document.querySelector('main') || document.body;
      host.insertBefore(el, host.firstChild);
    }
    el.textContent = msg;
  }

  /* ---------------- The reconciler: align cart state with rules ------------- */
  function reconcile() {
    if (busy) return;
    busy = true;

    getCart()
      .then(function (cart) {
        var hasCollagen = cartHasCollagen(cart);
        // Eligible ONLY when the flag is EXACTLY false (first-timer or guest).
        // undefined / true -> not eligible -> fail closed.
        var eligible = window.customerHasCollagenBefore === false;
        // Confirmed INELIGIBLE: a logged-in customer whose order history shows a
        // prior Collagen Pro purchase (flag is EXACTLY true). They already had
        // their gift, so the frother must be revalidated out of the cart. We act
        // only on a confirmed true (never on undefined) to avoid stripping a
        // legitimately-gifted frother when history is simply unknown.
        var ineligibleConfirmed = window.customerHasCollagenBefore === true;
        var anyFrother = frotherInCart(cart);
        var added = ssGet(SS_ADDED) === 'true';
        var dismissed = ssGet(SS_DISMISSED) === 'true';

        // --- Collagen NOT in cart -----------------------------------------
        if (!hasCollagen) {
          // Reset session intent so a future Collagen add starts fresh.
          ssRemove(SS_ADDED);
          ssRemove(SS_DISMISSED);
          // RULE 5: no Collagen Pro (direct or bundle) left -> strip EVERY
          // frother from the cart (the Frother is unlisted, so it's only ever
          // here because of this gift).
          var frothers = frotherLineItems(cart);
          if (frothers.length > 0) {
            return removeLineItemsByKey(
              frothers.map(function (i) {
                return i.key;
              })
            )
              .then(getCart)
              .then(notifyCartUpdated);
          }
          return; // nothing to do
        }

        // --- Collagen IS in cart ------------------------------------------
        if (anyFrother) {
          // REVALIDATION: a guest may have earned the gift, then logged in to an
          // account with prior Collagen history. They are no longer entitled, so
          // strip EVERY frother before checkout (the Frother is unlisted, so it
          // is only ever here because of this gift).
          if (ineligibleConfirmed) {
            ssRemove(SS_ADDED);
            ssSet(SS_DISMISSED, 'true'); // don't re-add for the rest of the session
            showMessage(
              'The complimentary frother is for first-time Collagen Pro ' +
                'customers, so we’ve removed it from your order. Everything ' +
                'else is unchanged.'
            );
            return removeLineItemsByKey(
              frotherLineItems(cart).map(function (i) {
                return i.key;
              })
            )
              .then(getCart)
              .then(notifyCartUpdated);
          }

          // A frother is present and Collagen is too — leave it. Remember it if
          // it's the one we gifted (so a later manual removal is respected).
          if (autoAddedFrothers(cart).length > 0) ssSet(SS_ADDED, 'true');

          // RULE: the complimentary frother is capped at ONE unit. If the
          // shopper bumped the quantity (or duplicate frother lines exist),
          // collapse back to a single unit: keep the first line at qty 1 and
          // zero out any extra quantity / duplicate lines.
          var frotherLines = frotherLineItems(cart);
          var totalFrotherQty = frotherLines.reduce(function (n, i) {
            return n + i.quantity;
          }, 0);
          if (totalFrotherQty > 1) {
            var ops = frotherLines.map(function (line, idx) {
              return { key: line.key, quantity: idx === 0 ? 1 : 0 };
            });
            return setLineQuantities(ops).then(getCart).then(notifyCartUpdated);
          }
          return;
        }

        // Collagen present, no frother in cart.
        if (added && !dismissed) {
          // We added one earlier and it's now gone while Collagen remains ->
          // the shopper removed it. Respect that; do not re-add this session.
          ssRemove(SS_ADDED);
          ssSet(SS_DISMISSED, 'true');
          return;
        }

        // RULE 4: first-timer / guest + Collagen present + no frother -> add one.
        if (eligible && !dismissed) {
          return addFrother()
            .then(function () {
              ssSet(SS_ADDED, 'true');
              return getCart();
            })
            .then(notifyCartUpdated)
            .catch(function (e) {
              // RULE 6: graceful OOS / failure — inform, don't break the cart.
              showMessage(
                'Heads up — we tried to add a complimentary frother but it’s ' +
                  'currently unavailable. The rest of your order is unaffected.'
              );
              console.warn('[frother-auto] add failed:', e.message);
            });
        }
        // Otherwise: nothing to do (state already correct / not eligible).
      })
      .catch(function (e) {
        // Network/parse failure: stay silent for the shopper, log for devs.
        console.error('[frother-auto] reconcile failed:', e);
      })
      .finally(function () {
        busy = false;
      });
  }

  /* ------------------------- Guest checkout gate ---------------------------- */
  // Eligibility can only be known once authenticated. So when a GUEST tries to
  // check out while the complimentary frother is in the cart, route them through
  // login and back to /cart first. On return the reconciler runs (the flag is
  // now accurate) and keeps or removes the gift BEFORE checkout — no surprise
  // removal mid-checkout. Logged-in shoppers are already reconciled on add, so
  // they check out untouched. The return-to-cart sign-in URL is built in Liquid
  // (window.VIVANA_LOGIN_RETURN_URL) because this store runs new customer
  // accounts, where return_to must be a relative path on /customer_authentication/login.
  //
  // We listen on BOTH 'click' (the checkout button) and 'submit' (the cart
  // form) in the capture phase, and decide from the LIVE cart rather than a
  // cached flag. This is what makes the cart PAGE and cart DRAWER behave
  // identically: the drawer is often used immediately after add — before the
  // load-time reconcile has cached cart state — so a cached flag could be stale
  // there and let the native submit slip through to Shopify's own login.
  var gateInFlight = false;

  var CHECKOUT_BTN_SELECTOR = '#checkout, button[name="checkout"], .cart__checkout-button';

  function isCheckoutTrigger(event) {
    var target = event.target;
    if (!target) return false;
    if (event.type === 'submit') {
      // Cart form submit. Guard against unrelated forms (discount, note, search)
      // and, when the submitter is known, require it to be the checkout button.
      if (!target.matches || !target.matches('form#cart-form, form.cart-form')) return false;
      if (event.submitter && event.submitter.matches) {
        return event.submitter.matches(CHECKOUT_BTN_SELECTOR);
      }
      return true; // Enter-key submit with unknown submitter on the cart form
    }
    // Click on (or within) the checkout button.
    return target.closest ? target.closest(CHECKOUT_BTN_SELECTOR) : null;
  }

  function guestCheckoutGate(event) {
    // Only gate confirmed guests; undefined (flag missing) -> don't interfere.
    if (window.customerLoggedIn !== false) return;
    if (!isCheckoutTrigger(event)) return;

    // Always intercept the guest's native checkout, then decide from the live
    // cart. (Login is required at checkout on this store anyway, so this adds no
    // extra step — it only changes WHERE the shopper lands after signing in.)
    event.preventDefault();
    event.stopImmediatePropagation();
    if (gateInFlight) return; // ignore double-fire (click + submit on same action)
    gateInFlight = true;

    getCart()
      .then(function (cart) {
        if (frotherInCart(cart)) {
          // Gift present -> validate eligibility first: login, then back to /cart.
          window.location.href = window.VIVANA_LOGIN_RETURN_URL || '/account/login';
        } else {
          // No gift to validate -> proceed to checkout as normal.
          window.location.href = '/checkout';
        }
      })
      .catch(function () {
        // Fail OPEN: never block a shopper from checking out on our account.
        window.location.href = '/checkout';
      })
      .finally(function () {
        // Release the latch once the decision has been made. It only needs to
        // absorb the synchronous click+submit double-fire of ONE action; left
        // set, it swallowed every later Checkout click on this page — e.g. a
        // shopper returning from checkout via Back (bfcache restores JS state)
        // or clicking again while the navigation was still pending.
        gateInFlight = false;
      });
  }

  document.addEventListener('click', guestCheckoutGate, true);
  document.addEventListener('submit', guestCheckoutGate, true);

  /* -------------------------------- Wiring ---------------------------------- */
  // Re-run when the cart changes via Ajax on the cart page (e.g. the shopper
  // removes Collagen), but ignore our own dispatched updates to avoid churn.
  document.addEventListener('cart:update', function (event) {
    if (event && event.detail && event.detail.data && event.detail.data.source === EVENT_SOURCE) return;
    reconcile();
  });

  // Perf (audit C5): the load-time reconcile used to run straight off
  // DOMContentLoaded, putting a long task (measured 269 ms) inside the TBT
  // window. Nothing about it is paint-critical — it only needs to run before
  // the shopper interacts with the cart — so it is scheduled on idle with a
  // 3 s ceiling. Cart mutations still reconcile instantly via the
  // `cart:update` listener above, and the guest checkout gate is already
  // bound, so no qualifying flow can slip through while we wait for idle.
  function scheduleInitialReconcile() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(reconcile, { timeout: 3000 });
    } else {
      window.setTimeout(reconcile, 1500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialReconcile);
  } else {
    scheduleInitialReconcile();
  }
})();
